import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, ModalVariant } from '@/components';
import { TextField } from '@/components/TextField';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface EditReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialName: string;
    initialDescription: string;
    onSubmit: (name: string, description: string | undefined) => Promise<void>;
}

interface EditReportFormValues {
    name: string;
    description: string;
}

export function EditReportModal({
    isOpen,
    onClose,
    initialName,
    initialDescription,
    onSubmit,
}: EditReportModalProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    const methods = useForm<EditReportFormValues>({
        defaultValues: { name: '', description: '' },
    });

    const {
        register,
        handleSubmit,
        reset,
        clearErrors,
        watch,
        formState: { errors, isSubmitting },
    } = methods;

    const nameValue = watch('name');

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        reset({
            name: initialName,
            description: initialDescription,
        });
    }, [isOpen, initialName, initialDescription, reset]);

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }
        reset({ name: '', description: '' });
        onClose();
    };

    const onValidSubmit = async (values: EditReportFormValues) => {
        const trimmedName = values.name.trim();
        const trimmedDescription = values.description.trim();
        await onSubmit(trimmedName, trimmedDescription || undefined);
        reset({ name: '', description: '' });
        onClose();
    };

    return (
        <FormProvider {...methods}>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={t('editModal.title')}
                variant={ModalVariant.PLAIN}
                onCancel={handleClose}
                onSubmit={() => void handleSubmit(onValidSubmit)()}
                submitText={t('editModal.submit')}
                submitDisabled={!nameValue?.trim() || isSubmitting}
                submitInProgress={isSubmitting}
                size="medium"
                closeOnOutsideClick
                contentTestId="edit-report-modal"
                submitButtonTestId="edit-report-submit"
            >
                <div className="flex flex-col gap-6 p-6">
                    <TextField
                        label={t('editModal.nameLabel')}
                        placeholder={t('editModal.namePlaceholder')}
                        {...register('name', {
                            required: true,
                            validate: (value) => !!value.trim(),
                            onChange: () => clearErrors('name'),
                        })}
                        error={errors.name?.message}
                        required
                        data-testid="edit-report-name-input"
                    />
                    <TextField
                        variant="area"
                        label={t('editModal.descriptionLabel')}
                        placeholder={t('editModal.descriptionPlaceholder')}
                        {...register('description', {
                            onChange: () => clearErrors('description'),
                        })}
                        error={errors.description?.message}
                        data-testid="edit-report-description-input"
                    />
                </div>
            </Modal>
        </FormProvider>
    );
}
